/* ==========================================================================
   MAISON NUYA — Système global
   Incipit Studio. Lot 1.

   Contenu : smooth scroll Lenis (desktop uniquement), gestion des panneaux
   latéraux, ajout au panier AJAX, helper carrousel, mesure des hauteurs
   d'en-tête. Aucune dépendance hors Lenis.
   ========================================================================== */

(function () {
  'use strict';

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqDesktop = window.matchMedia('(min-width: 1024px)');

  var Nuya = (window.Nuya = window.Nuya || {});

  Nuya.prefersReducedMotion = function () {
    return mqReduce.matches;
  };

  Nuya.isDesktop = function () {
    return mqDesktop.matches;
  };

  /* ----------------------------------------------------------------------
     Smooth scroll — Lenis, desktop uniquement, jamais si animations réduites
     ---------------------------------------------------------------------- */

  var lenis = null;

  function startLenis() {
    if (lenis || typeof window.Lenis === 'undefined') return;
    if (!mqDesktop.matches || mqReduce.matches) return;

    lenis = new window.Lenis({ duration: 1.1, smoothWheel: true });
    Nuya.lenis = lenis;

    function raf(time) {
      if (!lenis) return;
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }

  function stopLenis() {
    if (!lenis) return;
    lenis.destroy();
    lenis = null;
    Nuya.lenis = null;
  }

  function syncLenis() {
    if (mqDesktop.matches && !mqReduce.matches) startLenis();
    else stopLenis();
  }

  if (mqDesktop.addEventListener) {
    mqDesktop.addEventListener('change', syncLenis);
    mqReduce.addEventListener('change', syncLenis);
  }

  window.addEventListener('load', syncLenis);
  document.addEventListener('DOMContentLoaded', syncLenis);

  /* ----------------------------------------------------------------------
     Hauteurs — exposées en variables CSS pour les sections plein écran
     ---------------------------------------------------------------------- */

  function measureHeights() {
    var header = document.querySelector('[data-nuya-header]');
    var announcement = document.querySelector('[data-nuya-announcement]');
    var root = document.documentElement;

    root.style.setProperty('--nuya-header-h', (header ? header.offsetHeight : 0) + 'px');
    root.style.setProperty('--nuya-announcement-h', (announcement ? announcement.offsetHeight : 0) + 'px');
  }

  Nuya.measureHeights = measureHeights;

  document.addEventListener('DOMContentLoaded', measureHeights);
  window.addEventListener('load', measureHeights);
  window.addEventListener('resize', measureHeights);
  document.addEventListener('shopify:section:load', measureHeights);
  document.addEventListener('shopify:section:unload', measureHeights);

  /* ----------------------------------------------------------------------
     Panneaux latéraux — ouverture, fermeture, verrouillage du scroll
     ---------------------------------------------------------------------- */

  var openPanels = [];

  function lockScroll() {
    document.body.classList.add('nuya-no-scroll');
    if (lenis) lenis.stop();
  }

  function unlockScroll() {
    document.body.classList.remove('nuya-no-scroll');
    if (lenis) lenis.start();
  }

  var Panels = {
    open: function (panel) {
      if (!panel || openPanels.indexOf(panel) !== -1) return;

      panel.setAttribute('data-open', 'true');
      panel.removeAttribute('aria-hidden');

      var scrim = panel.getAttribute('data-scrim') ? document.querySelector(panel.getAttribute('data-scrim')) : null;
      if (scrim) scrim.setAttribute('data-open', 'true');

      openPanels.push(panel);
      lockScroll();

      var focusable = panel.querySelector('[data-nuya-autofocus], button, [href], input, select, textarea');
      if (focusable) {
        window.setTimeout(function () {
          focusable.focus({ preventScroll: true });
        }, 60);
      }

      panel.dispatchEvent(new CustomEvent('nuya:panel:open', { bubbles: true }));
    },

    close: function (panel) {
      if (!panel) return;

      panel.setAttribute('data-open', 'false');
      panel.setAttribute('aria-hidden', 'true');

      var scrim = panel.getAttribute('data-scrim') ? document.querySelector(panel.getAttribute('data-scrim')) : null;
      if (scrim) scrim.setAttribute('data-open', 'false');

      openPanels = openPanels.filter(function (item) {
        return item !== panel;
      });

      if (!openPanels.length) unlockScroll();

      var opener = panel.nuyaOpener;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });

      panel.dispatchEvent(new CustomEvent('nuya:panel:close', { bubbles: true }));
    },

    closeAll: function () {
      openPanels.slice().forEach(function (panel) {
        Panels.close(panel);
      });
    },

    toggle: function (panel) {
      if (!panel) return;
      if (panel.getAttribute('data-open') === 'true') Panels.close(panel);
      else Panels.open(panel);
    },
  };

  Nuya.panels = Panels;

  /* Ouverture / fermeture déclarative :
     [data-nuya-open="#selecteur"] et [data-nuya-close] */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-nuya-open]');
    if (opener) {
      var panel = document.querySelector(opener.getAttribute('data-nuya-open'));
      if (panel) {
        event.preventDefault();
        panel.nuyaOpener = opener;
        Panels.toggle(panel);
        return;
      }
    }

    var closer = event.target.closest('[data-nuya-close]');
    if (closer) {
      event.preventDefault();
      var target = closer.getAttribute('data-nuya-close');
      Panels.close(target ? document.querySelector(target) : closer.closest('[data-nuya-panel]'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (!openPanels.length) return;
    Panels.close(openPanels[openPanels.length - 1]);
  });

  /* ----------------------------------------------------------------------
     Panier — ajout AJAX, mise à jour du compteur
     ---------------------------------------------------------------------- */

  var Cart = {
    add: function (variantId, quantity) {
      return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart_add_failed');
          return response.json();
        })
        .then(function (item) {
          return Cart.refresh().then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('nuya:cart:added', { detail: { item: item, cart: cart }, bubbles: true })
            );
            /* Compatibilité avec les composants panier du thème de base. */
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart: cart }, bubbles: true }));
            return cart;
          });
        });
    },

    refresh: function () {
      return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          Cart.renderCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('nuya:cart:updated', { detail: { cart: cart }, bubbles: true }));
          return cart;
        });
    },

    renderCount: function (count) {
      document.querySelectorAll('[data-nuya-cart-count]').forEach(function (node) {
        node.textContent = count;
        node.toggleAttribute('hidden', !count);
      });
    },
  };

  Nuya.cart = Cart;

  /* ----------------------------------------------------------------------
     Carrousel — flèches et état désactivé
     ---------------------------------------------------------------------- */

  Nuya.initCarousel = function (root) {
    if (!root || root.nuyaCarouselReady) return;
    root.nuyaCarouselReady = true;

    var track = root.querySelector('[data-nuya-track]');
    var prev = root.querySelector('[data-nuya-prev]');
    var next = root.querySelector('[data-nuya-next]');
    if (!track) return;

    function step() {
      var item = track.querySelector('[data-nuya-item]');
      if (!item) return track.clientWidth;
      var gap = parseFloat(window.getComputedStyle(track).columnGap || '0') || 0;
      return item.offsetWidth + gap;
    }

    function updateArrows() {
      if (!prev || !next) return;
      var max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
      root.toggleAttribute('data-no-overflow', max <= 2);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: Nuya.prefersReducedMotion() ? 'auto' : 'smooth' });
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: Nuya.prefersReducedMotion() ? 'auto' : 'smooth' });
      });
    }

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();

    root.nuyaUpdateArrows = updateArrows;
  };

  /* ----------------------------------------------------------------------
     Helper d'initialisation compatible éditeur de thème
     ---------------------------------------------------------------------- */

  Nuya.onSection = function (selector, callback) {
    function run(scope) {
      (scope || document).querySelectorAll(selector).forEach(callback);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        run(document);
      });
    } else {
      run(document);
    }

    document.addEventListener('shopify:section:load', function (event) {
      run(event.target);
    });
  };

  document.addEventListener('DOMContentLoaded', function () {
    Cart.refresh();
  });
})();
